import React, { useCallback, useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';
import styled from 'styled-components';

import { FullPageModal } from '#/components/Modal/FullPageModal';
import { PlanPurchaseFlow } from '#/components/PlanPurchaseFlow/PlanPurchaseFlow';
import { Status } from '#/components/Status/Status';
import { useNavigation } from '#/hooks/useNavigation';
import { Path } from '#/service/routes';
import { paywallActions, usePaywallStore } from '#/store/paywall/paywall.store';

import { usePlanPickerActions } from './hooks';
import { useSubscriptionParameters } from './utils';

const HiddenContainer = styled.div`
  display: none;
`;

export const Paywall = React.memo(() => {
  const {
    isShowing,
    deferredPromise: { resolve: resolvePayment } = {},
    error,
  } = usePaywallStore();

  const { pathname } = useLocation();
  const prevPathName = useRef(pathname);
  const { goBack } = useNavigation('Paywall');

  const customParameters = useSubscriptionParameters();

  const actions = usePlanPickerActions();

  useEffect(() => {
    // Only react to the location change, nothing else. Otherwise, we will close the modal just after opening it
    if (isShowing && prevPathName.current !== pathname) {
      paywallActions.closePaywall();
    }
    prevPathName.current = pathname;
  }, [isShowing, pathname]);

  const handleErrorDismiss = useCallback(() => {
    paywallActions.setError(undefined);

    // Navigate to the previous page only if the paywall error
    // was dismissed on the playback page
    if (matchPath(Path.Playback, pathname)) {
      goBack();
    } else {
      paywallActions.closePaywall();
    }
  }, [goBack, pathname]);

  const showPaywall = isShowing || error;

  return (
    <div>
      <HiddenContainer
        className="hidden-paywall-container"
        data-testid="hiddenContainer"
      />
      {showPaywall && (
        <FullPageModal>
          {error && (
            <Status error={error} fatalError onDismiss={handleErrorDismiss} />
          )}
          <PlanPurchaseFlow
            actions={actions}
            customParameters={customParameters}
            onResult={resolvePayment}
          />
        </FullPageModal>
      )}
    </div>
  );
});

Paywall.displayName = 'Paywall';
